import * as React from 'react';
import { useConfirmModal } from '../../ConfirmModal/confirmModal';
import { WorkflowSavePayload } from '../../../types/ConfigureWorkflowTypes';

type UseWorkflowModalOptions = {
  initialState?: WorkflowSavePayload;
  onClose: () => void;
};

export type WrapSaveOptions = {
  errorMessage?: string;
  onSuccess?: () => void;
};

export const useWorkflowModal = ({
  initialState = null,
  onClose
}: UseWorkflowModalOptions) => {
  const [workflowState, setWorkflowState] =
    React.useState<WorkflowSavePayload>(initialState);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string>(null);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [isModalDisabled, setIsModalDisabled] = React.useState(false);

  const { confirm, ConfirmModal } = useConfirmModal();

  const reset = React.useCallback(() => {
    setIsSaving(false);
    setIsSaveDisabled(true);
    setErrorMessage(null);
    setHasChanges(false);
    setIsModalDisabled(false);
  }, []);

  const onWorkflowStateChange = React.useCallback(
    (update: Partial<WorkflowSavePayload>) =>
      setWorkflowState(prev => ({ ...prev, ...update })),
    []
  );

  const handleClose = React.useCallback(() => {
    if (hasChanges && !isModalDisabled) {
      confirm('Are you sure you want to cancel? You have not saved any data.', {
        header: 'Unsaved Changes',
        onConfirm: onClose
      });
    } else {
      onClose();
    }
  }, [hasChanges, isModalDisabled, confirm, onClose]);

  const wrapSave = React.useCallback(
    (saveFn: () => Promise<void>, options?: WrapSaveOptions) => async () => {
      setIsSaving(true);
      try {
        await saveFn();
        options?.onSuccess?.();
      } catch (err) {
        console.error('Failed to save workflow configuration', err);
        setErrorMessage(
          options?.errorMessage ??
            'Failed to save workflow configuration. Please try again.'
        );
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  return {
    workflowState,
    setWorkflowState,
    onWorkflowStateChange,
    isSaving,
    isSaveDisabled,
    setIsSaveDisabled,
    errorMessage,
    setErrorMessage,
    hasChanges,
    setHasChanges,
    isModalDisabled,
    setIsModalDisabled,
    handleClose,
    ConfirmModal,
    wrapSave,
    reset
  };
};
